import i18n from 'i18next';
import { UserInterface } from '@common-models/user';
import { isWhatsappExtensionContext } from './detect-context';
import { applyDocumentDirection } from './rtl';

// Supported languages based on available locale files
export const SUPPORTED_LANGUAGES = ['en', 'pt', 'es', 'de', 'it', 'fr', 'hi', 'he', 'ar'];

// Display metadata for ANY ISO-639 language code Whisper might output. Used by
// the voice-note transcription UI to render "🇮🇱 Hebrew" instead of a raw
// "he" code. Whisper internally uses lowercase ISO-639-1 codes; we map each
// to the most representative country flag + an English name. Unknown codes
// fall back to the raw code rendered as plain text (no flag).
export const LANGUAGE_DISPLAY: Record<string, { name: string; flag: string }> = {
  en: { name: 'English',     flag: '🇬🇧' },
  he: { name: 'Hebrew',      flag: '🇮🇱' },
  ar: { name: 'Arabic',      flag: '🇸🇦' },
  es: { name: 'Spanish',     flag: '🇪🇸' },
  pt: { name: 'Portuguese',  flag: '🇵🇹' },
  fr: { name: 'French',      flag: '🇫🇷' },
  de: { name: 'German',      flag: '🇩🇪' },
  it: { name: 'Italian',     flag: '🇮🇹' },
  ru: { name: 'Russian',     flag: '🇷🇺' },
  zh: { name: 'Chinese',     flag: '🇨🇳' },
  ja: { name: 'Japanese',    flag: '🇯🇵' },
  ko: { name: 'Korean',      flag: '🇰🇷' },
  hi: { name: 'Hindi',       flag: '🇮🇳' },
  tr: { name: 'Turkish',     flag: '🇹🇷' },
  pl: { name: 'Polish',      flag: '🇵🇱' },
  nl: { name: 'Dutch',       flag: '🇳🇱' },
  sv: { name: 'Swedish',     flag: '🇸🇪' },
  no: { name: 'Norwegian',   flag: '🇳🇴' },
  da: { name: 'Danish',      flag: '🇩🇰' },
  fi: { name: 'Finnish',     flag: '🇫🇮' },
  cs: { name: 'Czech',       flag: '🇨🇿' },
  el: { name: 'Greek',       flag: '🇬🇷' },
  uk: { name: 'Ukrainian',   flag: '🇺🇦' },
  ro: { name: 'Romanian',    flag: '🇷🇴' },
  hu: { name: 'Hungarian',   flag: '🇭🇺' },
  th: { name: 'Thai',        flag: '🇹🇭' },
  vi: { name: 'Vietnamese',  flag: '🇻🇳' },
  id: { name: 'Indonesian',  flag: '🇮🇩' },
  fa: { name: 'Persian',     flag: '🇮🇷' },
  ur: { name: 'Urdu',        flag: '🇵🇰' },
  bn: { name: 'Bengali',     flag: '🇧🇩' },
  ta: { name: 'Tamil',       flag: '🇮🇳' },
  te: { name: 'Telugu',      flag: '🇮🇳' },
};

/** Format a language code as `🇮🇱 Hebrew`; falls back to the raw code. */
export function formatLanguageDisplay(code: string | null | undefined): string {
  if (!code) return '';
  const norm = code.toLowerCase();
  const meta = LANGUAGE_DISPLAY[norm];
  if (!meta) return norm;
  return `${meta.flag} ${meta.name}`;
}

/**
 * Checks if a language code is supported
 */
export const isSupportedLanguage = (langCode: string): boolean => {
  // Normalize language code (e.g., 'pt-BR' -> 'pt', 'en-US' -> 'en')
  const normalizedLang = langCode.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(normalizedLang);
};

/**
 * Normalizes a language code to our supported format
 * e.g., 'pt-BR' -> 'pt', 'en-US' -> 'en'
 */
export const normalizeLanguageCode = (langCode: string): string => {
  return langCode.split('-')[0].toLowerCase();
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

const syncDirectionWithLanguage = async (language: string, dispatch?: any): Promise<void> => {
  const direction = applyDocumentDirection(language);

  if (!dispatch) return;

  try {
    const { setUserDirection } = await import(/* webpackMode: "eager" */ '@common-reducers/UserReducer');
    dispatch(setUserDirection(direction));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[language-detection] Failed syncing direction to store:', error);
    }
  }
};

/**
 * Coerces any language code into a supported value.
 * Returns the code itself when supported, otherwise `"en"`.
 */
const toSupportedLanguage = (lang: string | null | undefined): SupportedLanguage =>
  lang && isSupportedLanguage(lang) ? lang : 'en';

// ---------------------------------------------------------------------------
// Country → language map
// ---------------------------------------------------------------------------

/**
 * Maps country codes to language codes based on available languages.
 * Returns the language code if available, otherwise returns `'en'`.
 */
export const getLanguageFromCountry = (countryCode: string): string => {
  const countryToLanguageMap: Record<string, string> = {
    // Portuguese-speaking countries
    'BR': 'pt', 'PT': 'pt', 'AO': 'pt', 'MZ': 'pt',
    'CV': 'pt', 'GW': 'pt', 'ST': 'pt', 'TL': 'pt',
    // Spanish-speaking countries
    'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'CL': 'es',
    'PE': 'es', 'VE': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es',
    'BO': 'es', 'DO': 'es', 'HN': 'es', 'PY': 'es', 'SV': 'es',
    'NI': 'es', 'CR': 'es', 'PA': 'es', 'UY': 'es',
    // German-speaking countries
    'DE': 'de', 'AT': 'de', 'CH': 'de', 'LI': 'de', 'LU': 'de',
    // Italian-speaking countries
    'IT': 'it', 'SM': 'it', 'VA': 'it',
    // French-speaking countries
    'FR': 'fr', 'BE': 'fr', 'MC': 'fr', 'CD': 'fr', 'CG': 'fr',
    'CI': 'fr', 'CM': 'fr', 'SN': 'fr', 'MG': 'fr', 'BF': 'fr',
    'ML': 'fr', 'NE': 'fr', 'TD': 'fr', 'GA': 'fr', 'GN': 'fr',
    'BJ': 'fr', 'TG': 'fr', 'CF': 'fr', 'KM': 'fr', 'DJ': 'fr',
    'GQ': 'fr', 'HT': 'fr', 'RW': 'fr', 'SC': 'fr',
    'VU': 'fr',
    // Hindi-speaking countries
    'IN': 'hi',
    // Hebrew-speaking countries
    'IL': 'he',
    // Arabic-speaking countries
    'SA': 'ar', 'EG': 'ar', 'DZ': 'ar', 'IQ': 'ar', 'MA': 'ar',
    'SD': 'ar', 'YE': 'ar', 'SY': 'ar', 'TN': 'ar', 'JO': 'ar',
    'LY': 'ar', 'LB': 'ar', 'PS': 'ar', 'MR': 'ar', 'OM': 'ar',
    'KW': 'ar', 'AE': 'ar', 'BH': 'ar', 'QA': 'ar', 'SO': 'ar',
    // English-speaking countries
    'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en',
    'IE': 'en', 'ZA': 'en', 'NG': 'en', 'KE': 'en', 'GH': 'en',
    'UG': 'en', 'TZ': 'en', 'ZW': 'en', 'ZM': 'en', 'MW': 'en',
    'SG': 'en', 'MY': 'en', 'PH': 'en', 'PK': 'en', 'BD': 'en',
    'LK': 'en',
  };

  const language = countryToLanguageMap[countryCode.toUpperCase()];
  return language || 'en';
};

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

/**
 * Detects the user's country code from the browser locale.
 *
 * @returns Two-letter uppercase country code, or `""` if detection fails.
 */
export const detectUserCountry = (): string => {
  try {
    const locale = navigator.language || (navigator as any).userLanguage;
    if (locale) {
      const parts = locale.split('-');
      if (parts.length > 1) {
        return parts[1].toUpperCase();
      }
    }
    return '';
  } catch {
    return '';
  }
};

/**
 * Reads WhatsApp Web's language preference from **page** localStorage.
 *
 * Key:   `WALangPhonePref`
 * Value: `"<lang>_<COUNTRY>"` — e.g. `"pt_BR"`, `"he_IL"`, `"en_US"`
 *
 * The function extracts the language portion (before `"_"`) and returns it
 * verbatim. It does **not** validate against the supported list — callers
 * must validate with {@link isSupportedLanguage} or {@link toSupportedLanguage}.
 *
 * @returns Extracted lowercase language code, or `null` when the key is
 *          absent or localStorage is unavailable.
 */
export const detectLanguageFromWhatsApp = (): string | null => {
  try {
    const raw = localStorage.getItem('WALangPhonePref');
    if (!raw) return null;

    // WhatsApp stores the value as a JSON-encoded string with literal
    // quotes, e.g. '"hi"' or '"pt_BR"'. Strip them before parsing.
    const cleaned = raw.replace(/^"|"$/g, '');
    if (!cleaned) return null;

    const lang = cleaned.split('_')[0]?.toLowerCase();
    if (!lang) return null;

    if (process.env.NODE_ENV === 'development') {
      console.log('[language-detection] WALangPhonePref →', { raw, cleaned, lang });
    }

    return lang;
  } catch {
    // localStorage may be unavailable (sandboxed iframe, restricted context)
    return null;
  }
};

export const detectWhatsappLanguage = (): string | null => {
  try {
    const htmlLang = document.documentElement?.lang?.trim();
    if (htmlLang) return htmlLang;

    const fromWhatsappStorage = detectLanguageFromWhatsApp();
    if (fromWhatsappStorage) return fromWhatsappStorage;

    const dir = document.documentElement?.dir?.toLowerCase();
    if (dir === 'rtl') {
      const navLang = navigator.language?.split('-')[0]?.toLowerCase();
      if (navLang === 'he' || navLang === 'ar') return navLang;
      return 'he';
    }

    return null;
  } catch {
    return null;
  }
};

export const mapWhatsappLanguageToAppLanguage = (
  rawLang: string | null | undefined,
): string => {
  if (!rawLang) return 'en';
  const head = rawLang.replace('_', '-').split('-')[0]?.toLowerCase();
  if (!head) return 'en';
  return isSupportedLanguage(head) ? head : 'en';
};

// ---------------------------------------------------------------------------
// WhatsApp sync (standalone entry-point)
// ---------------------------------------------------------------------------

/**
 * Syncs the WhatsApp-detected language to i18n + DB **only when**
 * `user.preferredLanguage` is empty / undefined.
 *
 * If a preference already exists in the DB the function is a no-op —
 * user preference always wins.
 *
 * @param user     Authenticated user
 * @param dispatch Redux dispatch
 */
export const syncWhatsAppLanguageIfMissing = async (
  user: UserInterface,
  dispatch: any,
): Promise<void> => {
  // User already has a saved preference — respect it, do nothing.
  if (user.preferredLanguage) return;
  if (!user._id) return;
  if (!i18n.isInitialized) return;

  const waLang = detectLanguageFromWhatsApp();
  const langToSet = toSupportedLanguage(waLang);

  // Apply to i18n (only if different to avoid re-renders)
  if (i18n.language !== langToSet) {
    await i18n.changeLanguage(langToSet);
  }
  await syncDirectionWithLanguage(langToSet, dispatch);

  // Persist to localStorage
  try {
    localStorage.setItem('i18nextLng', langToSet);
  } catch {
    /* no-op — localStorage may be unavailable */
  }

  // Persist to DB
  try {
    const { DBUserThunks } = await import(/* webpackMode: "eager" */ '@common-reducers/DBServiceThunks');
    await dispatch(
      DBUserThunks.patch({
        entity: {
          _id: user._id,
          preferredLanguage: langToSet,
        },
      }),
    );
    if (process.env.NODE_ENV === 'development') {
      console.log('[language-detection] Saved language to DB:', langToSet);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[language-detection] DB patch failed:', error);
    }
  }
};

let extensionLanguageOverrideApplied = false;

export const applyExtensionWhatsappLanguage = async (
  dispatch?: any,
): Promise<boolean> => {
  if (!isWhatsappExtensionContext()) return false;
  if (extensionLanguageOverrideApplied) return false;
  if (!i18n.isInitialized) return false;

  const raw = detectWhatsappLanguage();
  const target = mapWhatsappLanguageToAppLanguage(raw);

  if (i18n.language !== target) {
    await i18n.changeLanguage(target);
  }
  await syncDirectionWithLanguage(target, dispatch);

  extensionLanguageOverrideApplied = true;
  if (process.env.NODE_ENV === 'development') {
    console.log('[language-detection] Extension WhatsApp override applied:', {
      raw,
      target,
    });
  }
  return true;
};

// ---------------------------------------------------------------------------
// Full initialisation (called once after login)
// ---------------------------------------------------------------------------

export const initializeUserLanguage = async (
  user: UserInterface | null | undefined,
  dispatch: any,
): Promise<void> => {
  if (!i18n.isInitialized) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[language-detection] i18n not initialised yet, skipping');
    }
    return;
  }

  if (isWhatsappExtensionContext()) {
    await applyExtensionWhatsappLanguage(dispatch);
    return;
  }

  if (!user?._id) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[language-detection] No user, skipping language setup');
    }
    return;
  }

  let languageToSet: string | null = null;

  // ------------------------------------------------------------------
  // Priority 1 — Saved DB preference
  // ------------------------------------------------------------------
  if (user.preferredLanguage) {
    // Validate that the preferred language is supported for UI
    if (isSupportedLanguage(user.preferredLanguage)) {
      languageToSet = normalizeLanguageCode(user.preferredLanguage);
      console.log('[language-detection] Using user preferred language:', languageToSet);
    } else {
      // User has a preference but it's not supported for UI
      // Fall through to detection logic
      console.log('[language-detection] User preferred language not supported for UI:', user.preferredLanguage);
    }
  }

  // ------------------------------------------------------------------
  // No valid DB preference → detect
  // ------------------------------------------------------------------

  if (!languageToSet) {
    // Priority 2 — Existing localStorage preference (set by manual switch)
    // This preserves the user's explicit choice even if the DB hasn't been
    // patched yet (e.g. after a manual switch from the LanguageSwitcher).
    try {
      const storedLng = localStorage.getItem('i18nextLng');
      if (storedLng && isSupportedLanguage(storedLng)) {
        languageToSet = normalizeLanguageCode(storedLng);
        console.log('[language-detection] Using localStorage i18nextLng:', languageToSet);
      }
    } catch { /* localStorage may be unavailable */ }
  }

  if (!languageToSet) {
    // Priority 3 — WhatsApp localStorage (WALangPhonePref)
    const waLang = detectLanguageFromWhatsApp();
    if (waLang && isSupportedLanguage(waLang)) {
      languageToSet = waLang;
      console.log('[language-detection] Using WhatsApp localStorage language:', languageToSet);
    }
  }

  if (!languageToSet) {
    // Priority 4 — Browser locale → country
    const countryCode = detectUserCountry();
    const detectedLanguage = getLanguageFromCountry(countryCode);
    languageToSet = detectedLanguage;
    console.log('[language-detection] Detected language from browser location:', {
      countryCode,
      detectedLanguage,
    });
  }

  // Fallback
  if (!languageToSet) {
    languageToSet = 'en';
  }

  // ------------------------------------------------------------------
  // Apply language
  // ------------------------------------------------------------------
  if (i18n.language !== languageToSet) {
    await i18n.changeLanguage(languageToSet);
    console.log('[language-detection] Language changed to:', languageToSet);
  }
  await syncDirectionWithLanguage(languageToSet, dispatch);

  // Persist to localStorage
  try {
    localStorage.setItem('i18nextLng', languageToSet);
  } catch {
    /* no-op */
  }

  // Always persist to DB when user has no saved preference yet.
  // This ensures even the 'en' default is saved, so subsequent
  // loads use the DB value directly and skip detection.
  if (!user.preferredLanguage) {
    try {
      const { DBUserThunks } = await import(/* webpackMode: "eager" */ '@common-reducers/DBServiceThunks');
      await dispatch(
        DBUserThunks.patch({
          entity: {
            _id: user._id,
            preferredLanguage: languageToSet,
          },
        }),
      );
      console.log('[language-detection] Saved resolved language to DB:', languageToSet);
    } catch (error) {
      console.warn('[language-detection] Failed to save language to DB:', error);
    }
  }
};

/**
 * Changes the user's preferred language and saves it to the database
 * This is used when the user manually selects a language from the dropdown
 *
 * @param language - The language code to set (e.g., 'en', 'pt', 'es')
 * @param userId - The user's ID (optional, for saving to database)
 * @param dispatch - Redux dispatch function (optional, for saving to database)
 * @returns Promise that resolves when language is changed
 */
export const changeUserLanguage = async (
  language: string,
  userId?: string,
  dispatch?: any
): Promise<void> => {
  try {
    const normalizedLanguage = normalizeLanguageCode(language);
    if (!isSupportedLanguage(normalizedLanguage)) {
      console.warn('[language-detection] Ignoring unsupported language change request:', language);
      return;
    }

    // Change the language in i18n
    await i18n.changeLanguage(normalizedLanguage);
    await syncDirectionWithLanguage(normalizedLanguage, dispatch);
    console.log('[language-detection] Language manually changed to:', normalizedLanguage);

    // Persist language preference to localStorage as fallback
    try {
      localStorage.setItem('i18nextLng', normalizedLanguage);
    } catch (error) {
      console.warn('[language-detection] Failed to save language preference to localStorage:', error);
    }

    // Save preferred language to database if user is logged in
    if (userId && dispatch) {
      try {
        const { DBUserThunks } = await import(/* webpackMode: "eager" */ "@common-reducers/DBServiceThunks");
        await dispatch(DBUserThunks.patch({
          entity: {
            _id: userId,
            preferredLanguage: normalizedLanguage,
          }
        }));
        console.log('[language-detection] Saved language preference to database:', normalizedLanguage);
      } catch (error) {
        console.warn('[language-detection] Failed to save language preference to database:', error);
      }
    }
  } catch (error) {
    console.error('[language-detection] Failed to change language:', error);
  }
};
